import { useState } from "react";

// Conversion factors to a common base unit
const WEIGHT_BASE = "g";
const VOLUME_BASE = "mL";

const WEIGHT_UNITS = {
  g:   1,
  kg:  1000,
  oz:  28.3495,
  lb:  453.592,
};

const VOLUME_UNITS = {
  mL:   1,
  L:    1000,
  cups: 236.588,
  tbsp: 14.7868,
  tsp:  4.92892,
  "fl oz": 29.5735,
  gal:  3785.41,
};

// Temperature conversions (special case — not multiplicative)
function convertTemp(value, from, to) {
  const n = parseFloat(value);
  if (isNaN(n)) return "";
  if (from === to) return n;
  // To °C first
  let c = from === "°F" ? (n - 32) * 5 / 9 : n;
  return to === "°F" ? (c * 9 / 5 + 32) : c;
}

const CATEGORIES = [
  { label: "Weight",      units: Object.keys(WEIGHT_UNITS) },
  { label: "Volume",      units: Object.keys(VOLUME_UNITS) },
  { label: "Temperature", units: ["°C", "°F"] },
];

function formatResult(n) {
  if (n === "" || n === null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs === 0) return "0";
  if (abs >= 1000) return parseFloat(n.toFixed(1)).toLocaleString();
  if (abs >= 10)   return parseFloat(n.toFixed(2)).toString();
  if (abs >= 1)    return parseFloat(n.toFixed(3)).toString();
  return parseFloat(n.toPrecision(4)).toString();
}

function convert(value, from, to) {
  const n = parseFloat(value);
  if (isNaN(n) || value === "") return "";

  if (WEIGHT_UNITS[from] && WEIGHT_UNITS[to]) {
    return n * WEIGHT_UNITS[from] / WEIGHT_UNITS[to];
  }
  if (VOLUME_UNITS[from] && VOLUME_UNITS[to]) {
    return n * VOLUME_UNITS[from] / VOLUME_UNITS[to];
  }
  if ((from === "°C" || from === "°F") && (to === "°C" || to === "°F")) {
    return convertTemp(n, from, to);
  }
  return null; // incompatible categories
}

export default function UnitConverter() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [fromUnit, setFromUnit] = useState("g");
  const [toUnit, setToUnit] = useState("oz");

  const result = convert(amount, fromUnit, toUnit);
  const incompatible = amount !== "" && result === null;

  function categoryOf(unit) {
    return CATEGORIES.find(c => c.units.includes(unit))?.label ?? null;
  }

  const fromCategory = categoryOf(fromUnit);
  const toCategory   = categoryOf(toUnit);

  // When the from-unit changes, reset to-unit to first unit of same category
  function handleFromChange(e) {
    const u = e.target.value;
    setFromUnit(u);
    const cat = CATEGORIES.find(c => c.units.includes(u));
    if (cat && !cat.units.includes(toUnit)) {
      setToUnit(cat.units.find(x => x !== u) ?? cat.units[0]);
    }
  }

  function swap() {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    if (result !== null && result !== "") {
      setAmount(formatResult(result));
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-600">Unit Converter</span>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="p-4 flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Amount input */}
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="Amount"
              className="w-28 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />

            {/* From unit */}
            <select
              value={fromUnit}
              onChange={handleFromChange}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              {CATEGORIES.map(cat => (
                <optgroup key={cat.label} label={cat.label}>
                  {cat.units.map(u => <option key={u} value={u}>{u}</option>)}
                </optgroup>
              ))}
            </select>

            {/* Swap button */}
            <button
              type="button"
              onClick={swap}
              className="text-gray-400 hover:text-sky-600 text-lg transition-colors px-1"
              title="Swap units"
            >⇄</button>

            {/* To unit — filtered to same category */}
            <select
              value={toUnit}
              onChange={e => setToUnit(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              {CATEGORIES.map(cat => (
                <optgroup key={cat.label} label={cat.label}>
                  {cat.units.map(u => <option key={u} value={u}>{u}</option>)}
                </optgroup>
              ))}
            </select>

            {/* Result */}
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-sm">=</span>
              {incompatible ? (
                <span className="text-sm text-red-400 italic">incompatible units</span>
              ) : (
                <span className="text-base font-semibold text-sky-700 tabular-nums">
                  {amount === "" ? "—" : formatResult(result)} <span className="text-sm font-normal text-gray-500">{toUnit}</span>
                </span>
              )}
            </div>
          </div>

          {/* Quick reference for common grower conversions */}
          <div className="text-xs text-gray-400 border-t border-gray-100 pt-3 flex flex-wrap gap-x-4 gap-y-1">
            <span>1 cup water ≈ 237 mL ≈ 237 g</span>
            <span>1 lb ≈ 454 g</span>
            <span>32°F = 0°C</span>
            <span>212°F = 100°C</span>
            <span>1 tbsp ≈ 15 mL</span>
          </div>
        </div>
      )}
    </div>
  );
}
